import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PencilIcon, UserAddIcon, OfficeBuildingIcon } from '@heroicons/react/outline';
import CompanyFormModal from '../components/CompanyFormModal';
import UserFormModalRBAC from '../components/UserFormModalRBAC';
import { useLanguage } from '../contexts/LanguageContext';

export default function CompanyDetail() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    fetchCompanyDetails();
  }, [id]);

  const fetchCompanyDetails = async () => {
    try {
      setIsLoading(true);
      
      // Fetch company details
      const companyResponse = await api.get(`/api/companies/${id}`);
      setCompany(companyResponse.data);
      
      // Fetch company users (direct company users, not branch users)
      const usersResponse = await api.get(`/api/users/company/${id}`);
      setCompanyUsers(usersResponse.data);
      
      // Fetch company branches
      const branchesResponse = await api.get(`/api/branches?companyId=${id}`);
      setBranches(branchesResponse.data);
      
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast.error(t('companies.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCompany = async (values) => {
    try {
      await api.put(`/api/companies/${id}`, values);
      toast.success(t('companies.companyUpdated'));
      setIsEditModalOpen(false);
      fetchCompanyDetails();
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error(error.response?.data?.message || t('companies.companyUpdateFailed'));
    }
  };

  const handleAddUser = async (values) => {
    try {
      // Make sure it's a company user
      values.role = 'company';
      values.companyId = parseInt(id);
      // Don't include branchId for company users
      delete values.branchId;
      
      await api.post('/api/users', values);
      toast.success(t('users.userAdded'));
      setIsAddUserModalOpen(false);
      fetchCompanyDetails();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.message || t('users.userAddFailed'));
    }
  };

  if (isLoading) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.loading')}</div>;
  }

  if (!company) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.noData')}</div>;
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{company.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('companyDetail.companyId')}: {company.id}</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-4 flex">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="btn-secondary mr-2 flex items-center"
          >
            <PencilIcon className="mr-2 h-5 w-5" />
            {t('companyDetail.editCompany')}
          </button>
          <button
            onClick={() => setIsAddUserModalOpen(true)}
            className="btn-primary flex items-center"
          >
            <UserAddIcon className="mr-2 h-5 w-5" />
            {t('companyDetail.addUser')}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('companyDetail.companyInformation')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('companyDetail.companyInfoDescription', { name: company.name })}</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('companyDetail.name')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{company.name}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('companyDetail.email')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{company.email}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('companyDetail.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{company.phone || 'N/A'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('companyDetail.address')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{company.address || 'N/A'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('companyDetail.status')}</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span
                  className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                    company.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {company.isActive ? t('companies.active') : t('companies.inactive')}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Company Users Section */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('companyDetail.companyUsers')}</h2>
        </div>
        
        {companyUsers.length === 0 ? (
          <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
            {t('companyDetail.noUsers')}
          </div>
        ) : (
          <div className="mt-4 flex flex-col">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('companyDetail.username')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('companyDetail.email')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('companyDetail.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {companyUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.username}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.email}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.isActive ? t('users.active') : t('users.inactive')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Branches Section */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('companyDetail.branches')}</h2>
          <Link
            to={`/branches?companyId=${id}`}
            className="btn-primary flex items-center"
          >
            <OfficeBuildingIcon className="mr-2 h-5 w-5" />
            {t('companyDetail.manageBranches')}
          </Link>
        </div>
        
        {branches.length === 0 ? (
          <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
            {t('companyDetail.noBranches')}
          </div>
        ) : (
          <div className="mt-4 flex flex-col">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.name')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.email')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.status')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('companyDetail.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {branches.map((branch) => (
                      <tr key={branch.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{branch.name}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{branch.email}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              branch.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {branch.isActive ? t('branches.active') : t('branches.inactive')}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <Link
                            to={`/branches/${branch.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {t('companyDetail.viewDetails')}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Company Modal */}
      <CompanyFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditCompany}
        initialValues={company}
      />

      {/* Add User Modal */}
      <UserFormModalRBAC
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSubmit={handleAddUser}
      />
    </div>
  );
} 